// Menu de acoes por linha de tabela — gatilho discreto (⋮) que abre um
// dropdown com as acoes secundarias (Editar, Inativar, etc). Substitui a
// fileira de botoes de texto na coluna AÇÕES: nunca quebra linha, tira o
// "muro de vermelho" (o Inativar vira um item perigoso dentro do menu) e
// convive com o Padrao de Grid (o clique no ⋮ nao seleciona/ativa a linha).
//
// Posicionamento `fixed` + getBoundingClientRect (vive dentro de container
// com overflow) e flipa pra cima se nao couber — convencao do sistema.
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import { MoreVertical } from 'lucide-react';

interface IconProps { size?: number }

export interface AcaoLinha {
  label: string;
  icon: ComponentType<IconProps>;
  onClick: () => void;
  /** item em vermelho (acao destrutiva, ex: Inativar) */
  perigoso?: boolean;
  /** nao renderiza o item (condicao de permissao/status) sem quebrar a lista */
  oculto?: boolean;
  disabled?: boolean;
}

interface Props {
  acoes: AcaoLinha[];
  /** rotulo do botao gatilho (tooltip/acessibilidade) */
  title?: string;
}

interface PosFixa { top: number; right: number; abrirCima: boolean }

function calcularPos(anchor: DOMRect, alturaEstimada: number): PosFixa {
  const margin = 4;
  const espacoBaixo = window.innerHeight - anchor.bottom;
  const abrirCima = espacoBaixo < alturaEstimada && anchor.top > espacoBaixo;
  const top = abrirCima ? anchor.top - margin : anchor.bottom + margin;
  return { top, right: window.innerWidth - anchor.right, abrirCima };
}

export function MenuAcoesLinha({ acoes, title = 'Ações' }: Props) {
  const visiveis = acoes.filter((a) => !a.oculto);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PosFixa | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function fora(e: MouseEvent) {
      const t = e.target as Node;
      if (painelRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function tecla(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    // Rolar a lista com o menu aberto deixaria o painel "solto" (posicao fixa);
    // melhor fechar.
    function rolou() { setOpen(false); }
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    window.addEventListener('scroll', rolou, true);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
      window.removeEventListener('scroll', rolou, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const alturaEstimada = visiveis.length * 40 + 8;
    setPos(calcularPos(btnRef.current.getBoundingClientRect(), alturaEstimada));
  }, [open, visiveis.length]);

  if (visiveis.length === 0) return null;

  function disparar(e: React.MouseEvent, acao: AcaoLinha) {
    e.stopPropagation();
    setOpen(false);
    acao.onClick();
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-componente="002"
        className="btn-icon menu-acoes-gatilho"
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <MoreVertical size={18} />
      </button>
      {open && pos && (
        <div
          ref={painelRef}
          role="menu"
          className="dropdown-painel menu-acoes-painel"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            // 'auto', nunca undefined: o `.dropdown-painel` do styles.css traz `top: 100%`, e sem o
            // inline ele vence o `bottom` — o menu que flipa pra cima abria colado na borda de
            // baixo da janela, fora da tela (as ultimas linhas de toda lista, 06/10/2026).
            top: pos.abrirCima ? 'auto' : pos.top,
            bottom: pos.abrirCima ? window.innerHeight - pos.top : undefined,
            right: pos.right,
            left: 'auto',
            margin: 0,
            minWidth: 176,
            zIndex: 200,
          }}
        >
          {visiveis.map((acao) => (
            <button
              key={acao.label}
              type="button"
              role="menuitem"
              disabled={acao.disabled}
              className={`dropdown-item ${acao.perigoso ? 'dropdown-item-perigo' : ''}`}
              onClick={(e) => disparar(e, acao)}
            >
              <acao.icon size={16} />
              {acao.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
